import { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarTimeEntry, RecurringTimeEntry, Resource } from '../../services/types';
import { CompanyOption, RecurringProgressEntry, getCalendarMonth, getRecurringEntries, getRecurringEntriesProgress, applyRecurringEntry, getSystemSettings, updateSystemSetting } from '../../services/api';
import { normalizeCompanyName } from './calendarUtils';
import { analyzeGaps, CompareData, CompanyGap, TemplateGap, GapStatus, MonthLabel, EntryDetail } from './compareUtils';

interface CompareViewProps {
  currentDate: Date;
  selectedResourceId: number | null;
  resources: Resource[];
  companies: CompanyOption[];
  onAddEntry: (date: Date, company: CompanyOption | null, prefill?: { hoursWorked: number }) => void;
  onEditEntry: (entry: CalendarTimeEntry) => void;
}

export default function CompareView({
  currentDate,
  selectedResourceId,
  resources,
  companies,
  onAddEntry,
  onEditEntry,
}: CompareViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compareData, setCompareData] = useState<CompareData | null>(null);

  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    on_track: true, // collapsed by default
  });

  // Expanded company detail rows
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());

  // Full entry lookup for edit modal
  const [entryLookup, setEntryLookup] = useState<Map<string, CalendarTimeEntry>>(new Map());

  // Template apply state
  const [applyingTemplates, setApplyingTemplates] = useState<Set<string>>(new Set());
  const [templateResults, setTemplateResults] = useState<Record<string, { success: boolean; message: string }>>({});

  // Company merge/alias state
  const [mergingCompany, setMergingCompany] = useState<string | null>(null);
  const [mergeSelections, setMergeSelections] = useState<Set<string>>(new Set());
  const [mergeSaving, setMergeSaving] = useState(false);

  const curYear = currentDate.getFullYear();
  const curMonth = currentDate.getMonth();

  const resourceName = useMemo(() => {
    if (!selectedResourceId) return '';
    const r = resources.find(res => res.autoTaskId === selectedResourceId);
    return r ? `${r.firstName} ${r.lastName}` : '';
  }, [selectedResourceId, resources]);

  const loadData = useCallback(async () => {
    if (!selectedResourceId) return;

    setLoading(true);
    setError(null);
    setTemplateResults({});
    setExpandedCompanies(new Set());

    try {
      const m2 = new Date(curYear, curMonth - 2, 1);
      const m1 = new Date(curYear, curMonth - 1, 1);
      const yearMonth = `${curYear}-${String(curMonth + 1).padStart(2, '0')}`;

      const [dataM2, dataM1, dataCur, templates, progress, settings] = await Promise.all([
        getCalendarMonth(m2.getFullYear(), m2.getMonth() + 1, selectedResourceId).catch(() => null),
        getCalendarMonth(m1.getFullYear(), m1.getMonth() + 1, selectedResourceId).catch(() => null),
        getCalendarMonth(curYear, curMonth + 1, selectedResourceId).catch(() => null),
        getRecurringEntries(selectedResourceId).catch(() => [] as RecurringTimeEntry[]),
        getRecurringEntriesProgress(selectedResourceId, yearMonth).catch(() => ({} as Record<string, RecurringProgressEntry>)),
        getSystemSettings().catch(() => ({} as Record<string, unknown>)),
      ]);

      // Build entry lookup map for edit modal
      const lookup = new Map<string, CalendarTimeEntry>();
      for (const md of [dataM2, dataM1, dataCur]) {
        for (const entry of md?.entries ?? []) {
          lookup.set(entry.id, entry);
        }
      }
      setEntryLookup(lookup);

      // Extract company aliases from settings
      const aliases = Array.isArray(settings.companyAliases) ? settings.companyAliases as string[][] : undefined;

      const result = analyzeGaps(
        [dataM2, dataM1, dataCur],
        currentDate,
        templates,
        progress,
        aliases
      );
      setCompareData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  }, [selectedResourceId, curYear, curMonth, currentDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleCompanyExpand = (companyName: string) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyName)) next.delete(companyName);
      else next.add(companyName);
      return next;
    });
  };

  const handleApplyTemplate = async (gap: TemplateGap) => {
    if (!selectedResourceId || !resourceName) return;

    const templateId = gap.template.id;
    setApplyingTemplates(prev => new Set([...prev, templateId]));

    try {
      const result = await applyRecurringEntry(
        templateId,
        gap.missingDates,
        selectedResourceId,
        resourceName
      );
      setTemplateResults(prev => ({
        ...prev,
        [templateId]: { success: true, message: `Created ${result.created} entries` },
      }));
      setTimeout(() => loadData(), 500);
    } catch (err) {
      setTemplateResults(prev => ({
        ...prev,
        [templateId]: { success: false, message: err instanceof Error ? err.message : 'Failed to apply' },
      }));
    } finally {
      setApplyingTemplates(prev => {
        const next = new Set(prev);
        next.delete(templateId);
        return next;
      });
    }
  };

  const handleClickEntry = (entryId: string) => {
    const fullEntry = entryLookup.get(entryId);
    if (fullEntry) {
      onEditEntry(fullEntry);
    }
  };

  const handleAddForCompany = (gap: CompanyGap, e: React.MouseEvent) => {
    e.stopPropagation(); // don't toggle row expansion
    // Match by companyId first (most reliable), fall back to normalized name
    const matchingCompany = companies.find(c =>
      (gap.companyId && c.autoTaskId === gap.companyId) ||
      normalizeCompanyName(c.companyName) === normalizeCompanyName(gap.companyName)
    );
    onAddEntry(new Date(), matchingCompany || null, { hoursWorked: Math.round(gap.priorAverage * 100) / 100 });
  };

  // All company names from compare data (for merge UI)
  const allCompanyNames = useMemo(() => {
    if (!compareData) return [];
    const names = new Set<string>();
    for (const gap of [...compareData.gaps.missing, ...compareData.gaps.partial, ...compareData.gaps.onTrack]) {
      names.add(gap.companyName);
    }
    return Array.from(names).sort();
  }, [compareData]);

  const handleStartMerge = (companyName: string) => {
    // Auto-select likely matches
    const autoSelected = new Set<string>();
    for (const other of allCompanyNames) {
      if (other !== companyName && companySimilarity(companyName, other) >= 0.35) {
        autoSelected.add(other);
      }
    }
    setMergingCompany(companyName);
    setMergeSelections(autoSelected);
  };

  const handleCancelMerge = () => {
    setMergingCompany(null);
    setMergeSelections(new Set());
  };

  const handleToggleMergeSelection = (name: string) => {
    setMergeSelections(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleSaveMerge = async () => {
    if (!mergingCompany || mergeSelections.size === 0) return;
    setMergeSaving(true);

    try {
      const settings = await getSystemSettings();
      const existingAliases: string[][] = Array.isArray(settings.companyAliases)
        ? (settings.companyAliases as string[][])
        : [];

      // All names in this merge (the source + selected targets)
      const mergeNames = new Set([mergingCompany, ...mergeSelections]);

      // Find all existing groups that overlap with our merge names
      const overlappingIndices: number[] = [];
      for (let i = 0; i < existingAliases.length; i++) {
        if (existingAliases[i].some(name => mergeNames.has(name))) {
          overlappingIndices.push(i);
        }
      }

      // Collect all names from overlapping groups + our new names
      const combined = new Set(mergeNames);
      for (const idx of overlappingIndices) {
        for (const name of existingAliases[idx]) {
          combined.add(name);
        }
      }

      // Build updated aliases: keep non-overlapping groups, add combined group
      const updatedAliases = existingAliases.filter((_, i) => !overlappingIndices.includes(i));
      updatedAliases.push(Array.from(combined).sort());

      await updateSystemSetting('companyAliases', updatedAliases, 'Company name alias groups for Compare view');

      setMergingCompany(null);
      setMergeSelections(new Set());
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save alias');
    } finally {
      setMergeSaving(false);
    }
  };

  // Early return states
  if (!selectedResourceId) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Select a resource to view billing comparison
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-slate-400">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading 3 months of data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-red-400">{error}</p>
        <button onClick={loadData} className="px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300">
          Retry
        </button>
      </div>
    );
  }

  if (!compareData) return null;

  const { months, gaps, totals, hasLimitedHistory } = compareData;
  const dayOfMonth = new Date().getDate();
  const isEarlyMonth = dayOfMonth < 10;

  const allCaughtUp = gaps.missing.length === 0 && gaps.partial.length === 0 && totals.templateGapCount === 0;

  return (
    <div className="flex-1 overflow-y-auto min-h-0">
      <div className="px-4 py-4 max-w-5xl mx-auto">
        {/* Advisory banners */}
        {isEarlyMonth && (
          <div className="mb-4 px-4 py-2 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm">
            Month is only {dayOfMonth} days in — gaps may fill as the month progresses.
          </div>
        )}
        {hasLimitedHistory && (
          <div className="mb-4 px-4 py-2 bg-blue-900/30 border border-blue-700/50 rounded-lg text-blue-300 text-sm">
            Limited history available — comparison uses available data only.
          </div>
        )}

        {/* Summary bar */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <SummaryCard label={months[0].label} value={`${totals.monthHours[0].toFixed(1)}h`} />
          <SummaryCard label={months[1].label} value={`${totals.monthHours[1].toFixed(1)}h`} />
          <SummaryCard
            label={months[2].label}
            value={`${totals.monthHours[2].toFixed(1)}h`}
            color={totals.coveragePercent >= 80 ? 'text-green-400' : totals.coveragePercent >= 50 ? 'text-amber-400' : 'text-red-400'}
            subtitle={`${totals.coveragePercent.toFixed(0)}% of prior avg`}
          />
          <SummaryCard
            label="Need Action"
            value={String(totals.gapCount)}
            color={totals.gapCount === 0 ? 'text-green-400' : 'text-red-400'}
            subtitle={totals.templateGapCount > 0 ? `${totals.templateGapCount} template gaps` : undefined}
          />
        </div>

        {/* All caught up */}
        {allCaughtUp && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-green-400 mb-1">All caught up!</h3>
            <p className="text-slate-400 text-sm">All recurring clients are billed for this month.</p>
          </div>
        )}

        {/* Gap sections */}
        {gaps.missing.length > 0 && (
          <GapSection
            title="MISSING"
            status="missing"
            gaps={gaps.missing}
            months={months}
            collapsed={!!collapsedSections.missing}
            expandedCompanies={expandedCompanies}
            onToggle={() => toggleSection('missing')}
            onToggleCompany={toggleCompanyExpand}
            onApplyTemplate={handleApplyTemplate}
            onAddEntry={handleAddForCompany}
            onClickEntry={handleClickEntry}
            applyingTemplates={applyingTemplates}
            templateResults={templateResults}
            mergingCompany={mergingCompany}
            mergeSelections={mergeSelections}
            mergeSaving={mergeSaving}
            allCompanyNames={allCompanyNames}
            onStartMerge={handleStartMerge}
            onCancelMerge={handleCancelMerge}
            onToggleMergeSelection={handleToggleMergeSelection}
            onSaveMerge={handleSaveMerge}
          />
        )}

        {gaps.partial.length > 0 && (
          <GapSection
            title="PARTIAL"
            status="partial"
            gaps={gaps.partial}
            months={months}
            collapsed={!!collapsedSections.partial}
            expandedCompanies={expandedCompanies}
            onToggle={() => toggleSection('partial')}
            onToggleCompany={toggleCompanyExpand}
            onApplyTemplate={handleApplyTemplate}
            onAddEntry={handleAddForCompany}
            onClickEntry={handleClickEntry}
            applyingTemplates={applyingTemplates}
            templateResults={templateResults}
            mergingCompany={mergingCompany}
            mergeSelections={mergeSelections}
            mergeSaving={mergeSaving}
            allCompanyNames={allCompanyNames}
            onStartMerge={handleStartMerge}
            onCancelMerge={handleCancelMerge}
            onToggleMergeSelection={handleToggleMergeSelection}
            onSaveMerge={handleSaveMerge}
          />
        )}

        {gaps.onTrack.length > 0 && (
          <GapSection
            title="ON TRACK"
            status="on_track"
            gaps={gaps.onTrack}
            months={months}
            collapsed={!!collapsedSections.on_track}
            expandedCompanies={expandedCompanies}
            onToggle={() => toggleSection('on_track')}
            onToggleCompany={toggleCompanyExpand}
            onApplyTemplate={handleApplyTemplate}
            onAddEntry={handleAddForCompany}
            onClickEntry={handleClickEntry}
            applyingTemplates={applyingTemplates}
            templateResults={templateResults}
            mergingCompany={mergingCompany}
            mergeSelections={mergeSelections}
            mergeSaving={mergeSaving}
            allCompanyNames={allCompanyNames}
            onStartMerge={handleStartMerge}
            onCancelMerge={handleCancelMerge}
            onToggleMergeSelection={handleToggleMergeSelection}
            onSaveMerge={handleSaveMerge}
          />
        )}
      </div>
    </div>
  );
}

// ─── Fuzzy matching ─────────────────────────────────────────────────────────

/** Tokenize a company name into comparable words, stripping legal suffixes */
function tokenize(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/[,.']/g, '')
    .replace(/\b(inc|llc|ltd|llp|corp|co|pc|pllc)\b/gi, '')
    .trim()
    .split(/\s+/)
    .filter(w => w.length > 0);
}

/** Bigram set for a string */
function bigrams(s: string): Set<string> {
  const lower = s.toLowerCase();
  const set = new Set<string>();
  for (let i = 0; i < lower.length - 1; i++) {
    set.add(lower.slice(i, i + 2));
  }
  return set;
}

/** Dice coefficient between two bigram sets */
function diceCoefficient(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 && b.size === 0) return 1;
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const bg of a) {
    if (b.has(bg)) intersection++;
  }
  return (2 * intersection) / (a.size + b.size);
}

/**
 * Compute similarity between two company names.
 * Combines word overlap and bigram similarity for robustness.
 * Returns 0-1, higher = more similar.
 */
function companySimilarity(a: string, b: string): number {
  const tokA = tokenize(a);
  const tokB = tokenize(b);

  // Word overlap (Jaccard-ish: shared words / total unique words)
  const setA = new Set(tokA);
  const setB = new Set(tokB);
  const union = new Set([...setA, ...setB]);
  let shared = 0;
  for (const w of setA) {
    if (setB.has(w)) shared++;
  }
  const wordScore = union.size > 0 ? shared / union.size : 0;

  // Bigram similarity on the full cleaned name
  const cleanA = tokA.join(' ');
  const cleanB = tokB.join(' ');
  const bigramScore = diceCoefficient(bigrams(cleanA), bigrams(cleanB));

  // One name is a subset of the other (e.g. "Gosch Ford" vs "Gosch Ford Temecula")
  const subsetScore = (shared === Math.min(setA.size, setB.size) && shared > 0) ? 0.5 : 0;

  return Math.max(wordScore, bigramScore, subsetScore);
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function SummaryCard({ label, value, color, subtitle }: { label: string; value: string; color?: string; subtitle?: string }) {
  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 px-4 py-3 text-center">
      <div className={`text-lg font-semibold ${color || 'text-white'}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{label}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
  );
}

interface GapSectionProps {
  title: string;
  status: GapStatus | 'on_track';
  gaps: CompanyGap[];
  months: [MonthLabel, MonthLabel, MonthLabel];
  collapsed: boolean;
  expandedCompanies: Set<string>;
  onToggle: () => void;
  onToggleCompany: (name: string) => void;
  onApplyTemplate: (gap: TemplateGap) => void;
  onAddEntry: (gap: CompanyGap, e: React.MouseEvent) => void;
  onClickEntry: (entryId: string) => void;
  applyingTemplates: Set<string>;
  templateResults: Record<string, { success: boolean; message: string }>;
  mergingCompany: string | null;
  mergeSelections: Set<string>;
  mergeSaving: boolean;
  allCompanyNames: string[];
  onStartMerge: (name: string) => void;
  onCancelMerge: () => void;
  onToggleMergeSelection: (name: string) => void;
  onSaveMerge: () => void;
}

function GapSection({ title, status, gaps, months, collapsed, expandedCompanies, onToggle, onToggleCompany, onApplyTemplate, onAddEntry, onClickEntry, applyingTemplates, templateResults, mergingCompany, mergeSelections, mergeSaving, allCompanyNames, onStartMerge, onCancelMerge, onToggleMergeSelection, onSaveMerge }: GapSectionProps) {
  const headerColor = status === 'missing' ? 'text-red-400 border-red-800/50' :
    status === 'partial' ? 'text-amber-400 border-amber-800/50' :
    'text-green-400 border-green-800/50';

  const bgColor = status === 'missing' ? 'bg-red-900/10' :
    status === 'partial' ? 'bg-amber-900/10' :
    'bg-green-900/10';

  return (
    <div className={`mb-4 rounded-lg border border-slate-700 overflow-hidden ${bgColor}`}>
      {/* Section header */}
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-800/50 transition-colors border-b ${headerColor}`}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-wide">{title}</span>
          <span className="text-xs text-slate-400">({gaps.length})</span>
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? '-rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Table content */}
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-xs text-slate-500 border-b border-slate-700/50">
                <th className="text-left px-4 py-2 font-medium">Company</th>
                <th className="text-right px-3 py-2 font-medium w-20">{months[0].label}</th>
                <th className="text-right px-3 py-2 font-medium w-20">{months[1].label}</th>
                <th className="text-right px-3 py-2 font-medium w-20">{months[2].label}</th>
                <th className="text-right px-3 py-2 font-medium w-20">Avg</th>
                <th className="text-right px-4 py-2 font-medium w-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map(gap => (
                <GapRow
                  key={gap.companyName}
                  gap={gap}
                  months={months}
                  isExpanded={expandedCompanies.has(gap.companyName)}
                  onToggleExpand={() => onToggleCompany(gap.companyName)}
                  onApplyTemplate={onApplyTemplate}
                  onAddEntry={onAddEntry}
                  onClickEntry={onClickEntry}
                  applyingTemplates={applyingTemplates}
                  templateResults={templateResults}
                  isMerging={mergingCompany === gap.companyName}
                  mergeSelections={mergeSelections}
                  mergeSaving={mergeSaving}
                  allCompanyNames={allCompanyNames}
                  onStartMerge={onStartMerge}
                  onCancelMerge={onCancelMerge}
                  onToggleMergeSelection={onToggleMergeSelection}
                  onSaveMerge={onSaveMerge}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function GapRow({ gap, months, isExpanded, onToggleExpand, onApplyTemplate, onAddEntry, onClickEntry, applyingTemplates, templateResults, isMerging, mergeSelections, mergeSaving, allCompanyNames, onStartMerge, onCancelMerge, onToggleMergeSelection, onSaveMerge }: {
  gap: CompanyGap;
  months: [MonthLabel, MonthLabel, MonthLabel];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onApplyTemplate: (tg: TemplateGap) => void;
  onAddEntry: (gap: CompanyGap, e: React.MouseEvent) => void;
  onClickEntry: (entryId: string) => void;
  applyingTemplates: Set<string>;
  templateResults: Record<string, { success: boolean; message: string }>;
  isMerging: boolean;
  mergeSelections: Set<string>;
  mergeSaving: boolean;
  allCompanyNames: string[];
  onStartMerge: (name: string) => void;
  onCancelMerge: () => void;
  onToggleMergeSelection: (name: string) => void;
  onSaveMerge: () => void;
}) {
  // Sort other companies by similarity to this company, with likely matches first
  const scoredCompanies = useMemo(() => {
    return allCompanyNames
      .filter(n => n !== gap.companyName)
      .map(name => ({ name, score: companySimilarity(gap.companyName, name) }))
      .sort((a, b) => b.score - a.score);
  }, [allCompanyNames, gap.companyName]);

  const suggestedMatches = scoredCompanies.filter(c => c.score >= 0.35);
  const otherCompanies = scoredCompanies.filter(c => c.score < 0.35);

  return (
    <>
      {/* Main company row — clickable to expand */}
      <tr
        className="border-b border-slate-700/30 hover:bg-slate-800/30 cursor-pointer"
        onClick={onToggleExpand}
      >
        <td className="px-4 py-3 text-base text-white">
          <div className="flex items-center gap-2">
            <svg className={`w-3.5 h-3.5 text-slate-500 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {gap.companyName}
          </div>
        </td>
        <td className="text-right px-3 py-3 text-base text-slate-300">{gap.hoursByMonth[0] > 0 ? `${gap.hoursByMonth[0].toFixed(1)}h` : '\u2014'}</td>
        <td className="text-right px-3 py-3 text-base text-slate-300">{gap.hoursByMonth[1] > 0 ? `${gap.hoursByMonth[1].toFixed(1)}h` : '\u2014'}</td>
        <td className={`text-right px-3 py-3 text-base ${gap.hoursByMonth[2] > 0 ? 'text-slate-300' : 'text-slate-500'}`}>
          {gap.hoursByMonth[2] > 0 ? `${gap.hoursByMonth[2].toFixed(1)}h` : '\u2014'}
        </td>
        <td className="text-right px-3 py-3 text-base text-slate-400">{gap.priorAverage.toFixed(1)}h</td>
        <td className="text-right px-4 py-3">
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={(e) => onAddEntry(gap, e)}
              className="px-2.5 py-1 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
              title="Open add-entry modal pre-filled with this company"
            >
              + Add
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onStartMerge(gap.companyName); }}
              className={`p-1 text-sm rounded transition-colors ${isMerging ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-indigo-400 hover:bg-slate-700'}`}
              title="Merge with another company name (alias)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
          </div>
        </td>
      </tr>

      {/* Merge alias panel */}
      {isMerging && (
        <tr className="border-b border-slate-700/30">
          <td colSpan={6} className="px-4 py-3 bg-indigo-950/20 border-l-2 border-indigo-500">
            <div className="space-y-1 max-h-56 overflow-y-auto mb-3">
              {/* Source company — always checked, not toggleable */}
              <label className="flex items-center gap-2 text-sm text-indigo-300 bg-indigo-900/30 px-2 py-1 rounded">
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="rounded border-indigo-600 bg-indigo-800 text-indigo-500"
                />
                {gap.companyName}
              </label>
              {/* Suggested matches (auto-checked) */}
              {suggestedMatches.map(({ name, score }) => (
                <label key={name} className="flex items-center gap-2 text-sm text-slate-300 bg-green-900/20 hover:bg-green-900/30 px-2 py-1 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mergeSelections.has(name)}
                    onChange={() => onToggleMergeSelection(name)}
                    className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="flex-1">{name}</span>
                  <span className="text-xs text-green-500">{Math.round(score * 100)}% match</span>
                </label>
              ))}
              {/* Remaining companies */}
              {otherCompanies.length > 0 && suggestedMatches.length > 0 && (
                <div className="text-xs text-slate-600 px-2 pt-1">Other companies</div>
              )}
              {otherCompanies.map(({ name }) => (
                <label key={name} className="flex items-center gap-2 text-sm text-slate-400 hover:bg-slate-800/30 px-2 py-1 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mergeSelections.has(name)}
                    onChange={() => onToggleMergeSelection(name)}
                    className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500"
                  />
                  {name}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={onCancelMerge}
                className="px-3 py-1 text-sm text-slate-400 hover:text-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onSaveMerge}
                disabled={mergeSelections.size === 0 || mergeSaving}
                className="px-3 py-1 text-sm bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
              >
                {mergeSaving ? 'Saving...' : 'Save Alias'}
              </button>
            </div>
          </td>
        </tr>
      )}

      {/* Expanded detail: side-by-side entry lists */}
      {isExpanded && (
        <tr className="border-b border-slate-700/30">
          <td colSpan={6} className="px-4 py-3 bg-slate-900/30">
            <div className="grid grid-cols-3 gap-4">
              {[0, 1, 2].map(mi => (
                <EntryColumn
                  key={mi}
                  label={months[mi].label}
                  entries={gap.entriesByMonth[mi]}
                  isCurrent={mi === 2}
                  onClickEntry={onClickEntry}
                />
              ))}
            </div>
          </td>
        </tr>
      )}

      {/* Template gap sub-rows */}
      {gap.linkedTemplates.map(tg => {
        const isApplying = applyingTemplates.has(tg.template.id);
        const result = templateResults[tg.template.id];

        return (
          <tr key={tg.template.id} className="border-b border-slate-700/20">
            <td colSpan={4} className="pl-10 pr-4 py-2">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span className="text-slate-600">&lfloor;</span>
                <span>{tg.template.templateName}</span>
                <span className="text-slate-500">applied {tg.appliedCount}/{tg.expectedCount}</span>
              </div>
            </td>
            <td colSpan={2} className="text-right px-4 py-2">
              {result ? (
                <span className={`text-xs ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                  {result.message}
                </span>
              ) : (
                <button
                  onClick={() => onApplyTemplate(tg)}
                  disabled={isApplying}
                  className="px-2.5 py-1 text-sm bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-wait text-white rounded transition-colors"
                >
                  {isApplying ? 'Applying...' : `Apply Missing (${tg.missingDates.length})`}
                </button>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function EntryColumn({ label, entries, isCurrent, onClickEntry }: { label: string; entries: EntryDetail[]; isCurrent: boolean; onClickEntry: (entryId: string) => void }) {
  const totalHours = entries.reduce((sum, e) => sum + e.hoursWorked, 0);

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-400">{label}</span>
        <span className={`text-xs font-medium ${isCurrent ? (entries.length > 0 ? 'text-blue-400' : 'text-red-400') : 'text-slate-400'}`}>
          {totalHours > 0 ? `${totalHours.toFixed(1)}h` : 'No entries'}
        </span>
      </div>
      {entries.length > 0 ? (
        <div className="space-y-1">
          {entries.map((e, i) => (
            <div
              key={i}
              onClick={() => onClickEntry(e.id)}
              className="text-xs bg-slate-800/50 rounded px-2 py-1.5 border border-slate-700/50 cursor-pointer hover:bg-slate-700/50 hover:border-slate-600 transition-colors"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-300 truncate">{e.ticketNumber}</span>
                <span className="text-slate-400 flex-shrink-0">{formatDate(e.dateWorked)} &middot; {e.hoursWorked}h</span>
              </div>
              {e.summaryNotes && (
                <div className="text-slate-500 truncate mt-0.5">{e.summaryNotes}</div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-slate-600 italic py-2">No entries this month</div>
      )}
    </div>
  );
}
